/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/


import React, { useState, useEffect } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { Globe, Zap, Menu, X, Calendar, ChevronLeft, ChevronRight, Atom } from 'lucide-react';
import FluidBackground from './components/FluidBackground';
import GradientText from './components/GlitchText';
import CustomCursor from './components/CustomCursor';
import ProfesseurCard from './components/ProfesseurCard';
import InstagramReel from './components/InstagramReel';
import SplashScreen from './components/SplashScreen';
import { Professeur } from './type';
import { APIProvider, Map, AdvancedMarker, Pin } from '@vis.gl/react-google-maps';

// Dummy Data
const groupe: Professeur[] = [
  {
    id: '1',
    name: 'prof horobate',
    matiere: 'Physique',
    username: '@instagram.com',
    instagramUrl: 'https://www.instagram.com/prof.7oroubat',
    image: '/ilyass.jpg',
    description: 'Ilyass est un professeur de physique très connu dans le milieu scolaire et sur les réseaux sociaux, il est spécialisé dans l\'enseignement de la physique et il est très apprécié par ses élèves pour sa pédagogie et son humour.'
  },
  {
    id: '2',
    name: 'Moul   SVT',
    matiere: 'SVT',
    username: '@instagram.com',
    instagramUrl: 'https://www.instagram.com/moul.svt',
    image: '/hamada.jpg',
    description: 'Hamada est un professeur de svt très connu dans le milieu scolaire et sur les réseaux sociaux, il est spécialisé dans l\'enseignement de la svt et il est très apprécié par ses élèves pour sa pédagogie et son humour.'
  },
  {
    id: '3',
    name: 'Oussama Rahibe',
    matiere: 'MATHS',
    username: '@instagram.com',
    instagramUrl: 'https://www.instagram.com/rahibe_maths',
    image: '/oussama.jpg',
    description: 'Oussama est un professeur de mathématiques très connu dans le milieu scolaire et sur les réseaux sociaux, il est spécialisé dans l\'enseignement des mathématiques et il est très apprécié par ses élèves pour sa pédagogie et son humour.'
  }
];

const ecoles = [
  { id: 1, name: "Centre Passbac - Sidi maarof", lat: 33.5866, lng: -7.6300, address: "Quartier Maarif, Casablanca" },
  { id: 2, name: "Centre Passbac - Iraki", lat: 33.5950, lng: -7.6450, address: "Bourgogne, Casablanca" },
];

const App: React.FC = () => {
  const { scrollYProgress } = useScroll();
  const y = useTransform(scrollYProgress, [0, 1], [0, -100]);
  const opacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedProfesseur, setSelectedProfesseur] = useState<Professeur | null>(null);
  const [niveau, setNiveau] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // Handle keyboard navigation for Professeur modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!selectedProfesseur) return;
      if (e.key === 'ArrowLeft') navigateProfesseur('prev');
      if (e.key === 'ArrowRight') navigateProfesseur('next');
      if (e.key === 'Escape') setSelectedProfesseur(null);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProfesseur]);

  const scrollToSection = (id: string) => {
    setMobileMenuOpen(false);
    const element = document.getElementById(id);
    if (element) {
      const headerOffset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.scrollY - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const navigateProfesseur = (direction: 'next' | 'prev') => {
    if (!selectedProfesseur) return;
    const currentIndex = groupe.findIndex(a => a.id === selectedProfesseur.id);
    let nextIndex;
    if (direction === 'next') {
      nextIndex = (currentIndex + 1) % groupe.length;
    } else {
      nextIndex = (currentIndex - 1 + groupe.length) % groupe.length;
    }
    setSelectedProfesseur(groupe[nextIndex]);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Validation manuelle du niveau (l'input hidden avec required peut causer un scroll indesirable)
    if (!niveau) {
      alert('Veuillez choisir un niveau scolaire.');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    const form = e.currentTarget;
    const formData = new FormData(form);
    // S'assurer que le niveau est bien dans le FormData
    formData.set('niveau', niveau);

    try {
      const scriptURL = 'https://script.google.com/macros/s/AKfycbzKfMapD30lbCpPCUzy7jtqFfPzNtSLlWYVF6bjXWHI8r9P-62KyAEi_t4O02NHKJkGaw/exec';

      await fetch(scriptURL, {
        method: 'POST',
        mode: 'no-cors',
        body: formData,
      });

      setSubmitStatus('success');
      form.reset();
      setNiveau('');

      // Masquer le message de succès après 5 secondes
      setTimeout(() => setSubmitStatus('idle'), 5000);
    } catch (error) {
      console.error('Erreur:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SplashScreen onComplete={() => { }} />
      <div className="relative min-h-screen text-white selection:bg-[#4fb7b3] selection:text-black cursor-auto md:cursor-none overflow-x-hidden">
        <CustomCursor />
        <FluidBackground />

        {/* Navigation */}
        <nav className="fixed top-0 left-0 right-0 z-40 flex items-center justify-between px-6 md:px-8 py-6 mix-blend-difference">
          <div className="font-heading text-xl md:text-2xl font-bold tracking-tighter text-white cursor-default z-50">PASSBAC</div>

          {/* Desktop Menu */}
          <div className="hidden md:flex gap-10 text-sm font-bold tracking-widest uppercase">
            {[
              { id: 'groupe', label: 'GROUPE' },
              { id: 'experience', label: 'EXPERIENCE' },
              { id: 'actualites', label: 'ACTUALITÉS' },
              { id: 'contact', label: 'CONTACT' }
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className="hover:text-[#a8fbd3] transition-colors text-white cursor-pointer bg-transparent border-none"
                data-hover="true"
              >
                {item.label}
              </button>
            ))}
          </div>
          <button
            onClick={() => scrollToSection('contact')}
            className="hidden md:inline-block border border-white px-8 py-3 text-xs font-bold tracking-widest uppercase hover:bg-white hover:text-black transition-all duration-300 text-white cursor-pointer bg-transparent"
            data-hover="true"
          >
            NOUS CONTACTER
          </button>

          {/* Mobile Menu Toggle */}
          <button
            className="md:hidden text-white z-50 relative w-10 h-10 flex items-center justify-center"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X /> : <Menu />}
          </button>
        </nav>

        {/* Mobile Menu Overlay */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="fixed inset-0 z-30 bg-[#31326f]/95 backdrop-blur-xl flex flex-col items-center justify-center gap-8 md:hidden"
            >
              {[
                { id: 'groupe', label: 'GROUPE' },
                { id: 'experience', label: 'EXPERIENCE' },
                { id: 'actualites', label: 'ACTUALITÉS' },
                { id: 'contact', label: 'CONTACT' }
              ].map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className="text-4xl font-heading font-bold text-white hover:text-[#a8fbd3] transition-colors bg-transparent border-none"
                >
                  {item.label}
                </button>
              ))}
              <button
                onClick={() => scrollToSection('contact')}
                className="mt-8 border border-white px-10 py-4 text-sm font-bold tracking-widest uppercase bg-white text-black"
              >
                Get contact
              </button>

              <div className="absolute bottom-10 flex gap-6">
                <a href="https://www.instagram.com/pass_bac" className="text-white/50 hover:text-white transition-colors">Instagram</a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* HERO SECTION */}
        <header className="relative h-[100svh] min-h-[600px] flex flex-col items-center justify-center overflow-hidden px-4">
          <motion.div
            style={{ y, opacity }}
            className="z-10 text-center flex flex-col items-center w-full max-w-6xl pb-24 md:pb-20"
          >
            {/* Date / Location */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.2 }}
              className="flex items-center gap-3 md:gap-6 text-xs md:text-base font-mono text-[#a8fbd3] tracking-[0.2em] md:tracking-[0.3em] uppercase mb-4 bg-black/20 px-4 py-2 rounded-full backdrop-blur-sm"
            >
              <span>BACCALAUREAT</span>
              <span className="w-1.5 h-1.5 md:w-2 md:h-2 bg-[#4fb7b3] rounded-full animate-pulse" />
              <span>2026</span>
            </motion.div>

            {/* Main Title */}
            <div className="relative w-full flex justify-center items-center">
              <GradientText
                text="PASSBAC"
                as="h1"
                className="text-[15vw] md:text-[14vw] leading-[0.9] font-black tracking-tighter text-center"
              />
              {/* Optimized Orb - Reduced Blur for Performance */}
              <motion.div
                className="absolute -z-20 w-[50vw] h-[50vw] bg-white/5 blur-[40px] rounded-full pointer-events-none will-change-transform"
                animate={{ scale: [0.8, 1.2, 0.8], opacity: [0.3, 0.6, 0.3] }}
                transition={{ duration: 6, repeat: Infinity }}
                style={{ transform: 'translateZ(0)' }}
              />
            </div>

            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 1.5, delay: 0.5, ease: "circOut" }}
              className="w-full max-w-md h-px bg-gradient-to-r from-transparent via-white/50 to-transparent mt-4 md:mt-8 mb-6 md:mb-8"
            />

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 1 }}
              className="text-base md:text-2xl font-light max-w-xl mx-auto text-white/90 leading-relaxed drop-shadow-lg px-4"
            >
              Accompagnement sur mesure vers la réussite
            </motion.p>
          </motion.div>

          {/* MARQUEE - SLOWED DOWN for Performance & Aesthetics */}
          <div className="absolute bottom-12 md:bottom-16 left-0 w-full py-4 md:py-6 bg-white text-black z-20 overflow-hidden border-y-4 border-black shadow-[0_0_40px_rgba(255,255,255,0.4)]">
            <motion.div
              className="flex w-fit will-change-transform"
              animate={{ x: "-50%" }}
              transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
            >
              {/* Duplicate content for seamless loop */}
              {[0, 1].map((key) => (
                <div key={key} className="flex whitespace-nowrap shrink-0">
                  {[...Array(4)].map((_, i) => (
                    <span key={i} className="text-3xl md:text-7xl font-heading font-black px-8 flex items-center gap-4">
                      PASSBAC 2026 <span className="text-black text-2xl md:text-4xl">●</span>
                      ACCOMPAGNEMENT VERS LA REUSSITE <span className="text-black text-2xl md:text-4xl">●</span>
                    </span>
                  ))}
                </div>
              ))}
            </motion.div>
          </div>
        </header>

        {/* groupe SECTION */}
        <section id="groupe" className="relative z-10 py-20 md:py-32">
          <div className="max-w-[1600px] mx-auto px-4 md:px-6">
            <div className="flex flex-col md:flex-row justify-between items-end mb-12 md:mb-16 px-4">
              <h2 className="text-5xl md:text-8xl font-heading font-bold uppercase leading-[0.9] drop-shadow-lg break-words w-full md:w-auto">
                GROUPE <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#a8fbd3] to-[#4fb7b3]">PASSBAC</span>
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-white/10 bg-black/20 backdrop-blur-sm">
              {groupe.map((Professeur) => (
                <ProfesseurCard key={Professeur.id} Professeur={Professeur} onClick={() => setSelectedProfesseur(Professeur)} />
              ))}
            </div>
          </div>
        </section>

        {/* EXPERIENCE SECTION */}
        <section id="experience" className="relative z-10 py-20 md:py-32 bg-black/20 backdrop-blur-sm border-t border-white/10 overflow-hidden">
          {/* Decorative blurred circle - Optimized */}
          <div className="absolute top-1/2 right-[-20%] w-[50vw] h-[50vw] bg-[#4fb7b3]/20 rounded-full blur-[40px] pointer-events-none will-change-transform" style={{ transform: 'translateZ(0)' }} />

          <div className="max-w-7xl mx-auto px-4 md:px-6 relative">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 md:gap-16 items-center">
              <div className="lg:col-span-5 order-2 lg:order-1">
                <h2 className="text-4xl md:text-7xl font-heading font-bold mb-6 md:mb-8 leading-tight">
                  VERS <br /> <GradientText text="L'AVANT" className="text-5xl md:text-8xl" />
                </h2>
                <p className="text-lg md:text-xl text-gray-200 mb-8 md:mb-12 font-light leading-relaxed drop-shadow-md">
                  PASSBAC est un groupe d'exellence qui vous accopagne et vous ouvre les voix vers l'excellence
                </p>

                <div className="space-y-6 md:space-y-8">
                  {[
                    { icon: Globe, title: 'Professeurs Expert', desc: 'Des professeurs qualifiés et expérimentés pour vous accompagner dans votre parcours.' },
                    { icon: Zap, title: 'Environnement convenable', desc: 'Un environnement propice à l\'apprentissage et à la réussite.' },
                    { icon: Atom, title: 'Accompagnement personnalisé', desc: 'Un accompagnement personnalisé pour répondre à vos besoins.' },
                  ].map((feature, i) => (
                    <div
                      key={i}
                      className="flex items-start gap-6"
                    >
                      <div className="p-4 rounded-2xl bg-white/10 backdrop-blur-md border border-white/5">
                        <feature.icon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h4 className="text-lg md:text-xl font-bold mb-1 md:mb-2 font-heading">{feature.title}</h4>
                        <p className="text-sm text-gray-300">{feature.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="lg:col-span-7 relative h-[400px] md:h-[700px] w-full order-1 lg:order-2">
                <div className="absolute inset-0 bg-gradient-to-br from-[#637ab9] to-[#4fb7b3] rounded-3xl rotate-3 opacity-30 blur-xl" />
                <div className="relative h-full w-full rounded-3xl overflow-hidden border border-white/10 group shadow-2xl">
                  <img
                    src="/reussite.jpg"
                    alt="Crowd"
                    className="h-full w-full object-cover transition-transform duration-[1.5s] group-hover:scale-110 will-change-transform"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-80" />

                  <div className="absolute bottom-6 left-6 md:bottom-10 md:left-10">
                    <div className="text-5xl md:text-8xl font-heading font-bold text-transparent bg-clip-text bg-gradient-to-b from-white to-white/0 opacity-50">
                      26
                    </div>
                    <div className="text-lg md:text-xl font-bold tracking-widest uppercase mt-2 text-white">
                      Notre Objectif
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ACTUALITES SECTION */}
        <section id="actualites" className="relative z-10 py-16 md:py-24 bg-black/20 backdrop-blur-sm border-t border-white/10 overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10 md:mb-16">
              <h2 className="text-4xl md:text-8xl font-heading font-bold opacity-20 text-white">
                ACTUALITÉ
              </h2>
              <p className="text-[#a8fbd3] font-mono uppercase tracking-widest -mt-3 md:-mt-6 relative z-10 text-xs md:text-sm">
                Suivez-nous sur Instagram
              </p>
            </div>
            <InstagramReel />
          </div>
        </section>

        {/* contact SECTION */}
        <section id="contact" className="relative z-10 py-20 md:py-32 px-4 md:px-6 bg-black/30 backdrop-blur-lg">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12 md:mb-20">
              <h2 className="text-5xl md:text-9xl font-heading font-bold opacity-20 text-white">
                CONTACT
              </h2>
              <p className="text-[#a8fbd3] font-mono uppercase tracking-widest -mt-3 md:-mt-8 relative z-10 text-sm md:text-base">
                Contactez nous pour plus d'informations
              </p>
            </div>

            <form onSubmit={handleSubmit} className="w-full max-w-5xl mx-auto space-y-8 md:space-y-10 bg-black/40 hover:bg-white/5 transition-all duration-500 p-8 md:p-14 rounded-[2rem] border border-white/5 hover:border-white/20 hover:shadow-[0_0_40px_rgba(79,183,179,0.15)] backdrop-blur-md mb-20 relative z-20 group">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                <div className="flex flex-col gap-3">
                  <label htmlFor="nom" className="text-xs md:text-sm font-mono tracking-widest text-[#a8fbd3] uppercase">Nom</label>
                  <input
                    type="text"
                    id="nom"
                    name="nom"
                    placeholder="Votre nom"
                    className="bg-white/5 border border-white/10 p-5 rounded-xl text-white focus:outline-none focus:border-[#4fb7b3] hover:bg-white/10 transition-all font-light placeholder:text-white/20 focus:shadow-[0_0_15px_rgba(79,183,179,0.2)]"
                    required
                  />
                </div>
                <div className="flex flex-col gap-3">
                  <label htmlFor="prenom" className="text-xs md:text-sm font-mono tracking-widest text-[#a8fbd3] uppercase">Prénom</label>
                  <input
                    type="text"
                    id="prenom"
                    name="prenom"
                    placeholder="Votre prénom"
                    className="bg-white/5 border border-white/10 p-5 rounded-xl text-white focus:outline-none focus:border-[#4fb7b3] hover:bg-white/10 transition-all font-light placeholder:text-white/20 focus:shadow-[0_0_15px_rgba(79,183,179,0.2)]"
                    required
                  />
                </div>
              </div>

              <div className="flex flex-col gap-3">
                <label htmlFor="telephone" className="text-xs md:text-sm font-mono tracking-widest text-[#a8fbd3] uppercase">Numéro de téléphone</label>
                <input
                  type="tel"
                  id="telephone"
                  name="telephone"
                  placeholder="Ex: 06 00 00 00 00"
                  className="bg-white/5 border border-white/10 p-5 rounded-xl text-white focus:outline-none focus:border-[#4fb7b3] hover:bg-white/10 transition-all font-light placeholder:text-white/20 focus:shadow-[0_0_15px_rgba(79,183,179,0.2)]"
                  required
                />
              </div>

              <div className="flex flex-col gap-4 mt-8">
                <label className="text-xs md:text-sm font-mono tracking-widest text-[#a8fbd3] uppercase mb-2">Niveau scolaire</label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {['Tronc Commun', '1ère Année Baccalauréat', '2ème Année Baccalauréat'].map((level) => (
                    <button
                      key={level}
                      type="button"
                      onMouseDown={(e) => {
                        // Empêcher le focus sur le bouton pour éviter le scroll automatique du navigateur
                        e.preventDefault();
                        setNiveau(level);
                      }}
                      className={`relative p-5 md:p-8 rounded-xl border transition-all duration-300 flex flex-col items-center justify-center gap-2 overflow-hidden ${niveau === level
                        ? 'border-[#4fb7b3] bg-[#4fb7b3]/10 shadow-[0_0_30px_rgba(79,183,179,0.2)]'
                        : 'border-white/10 bg-white/5 hover:bg-white/10 hover:border-white/30 hover:-translate-y-1 hover:shadow-[0_10px_20px_rgba(0,0,0,0.3)]'
                        }`}
                    >
                      <div className={`absolute inset-0 bg-gradient-to-br from-[#4fb7b3]/20 to-transparent transition-opacity duration-500 ${niveau === level ? 'opacity-100' : 'opacity-0 hover:opacity-40'}`} />
                      <span className={`relative z-10 text-sm md:text-base font-bold tracking-wider text-center transition-colors duration-300 ${niveau === level ? 'text-[#a8fbd3] drop-shadow-[0_0_8px_rgba(168,251,211,0.5)]' : 'text-white/70 hover:text-white'}`}>
                        {level}
                      </span>
                      {niveau === level && (
                        <div
                          className="absolute bottom-0 left-0 right-0 h-1 bg-[#4fb7b3] shadow-[0_0_15px_#4fb7b3]"
                        />
                      )}
                    </button>
                  ))}
                </div>
                {/* Input hidden SANS required - validation manuelle dans handleSubmit */}
                <input type="hidden" name="niveau" value={niveau} />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full bg-gradient-to-r from-[#4fb7b3] to-[#a8fbd3] hover:from-[#3ca5a0] hover:to-[#91e1be] text-black transition-all duration-300 p-6 rounded-xl font-black tracking-[0.2em] uppercase mt-10 drop-shadow-[0_0_15px_rgba(79,183,179,0.3)] hover:drop-shadow-[0_0_30px_rgba(79,183,179,0.6)] transform border-none cursor-pointer ${isSubmitting ? 'opacity-70 cursor-not-allowed' : 'hover:-translate-y-1'} text-lg`}
              >
                {isSubmitting ? 'Envoi en cours...' : 'S\'inscrire'}
              </button>

              {/* Status Messages */}
              {submitStatus === 'success' && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-6 p-4 rounded-xl bg-[#4fb7b3]/20 border border-[#4fb7b3]/50 text-[#a8fbd3] text-center font-mono text-sm"
                >
                  Votre demande a été envoyée avec succès !
                </motion.div>
              )}
              {submitStatus === 'error' && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-6 p-4 rounded-xl bg-red-500/20 border border-red-500/50 text-red-200 text-center font-mono text-sm"
                >
                  Une erreur est survenue lors de l'envoi. Veuillez réessayer.
                </motion.div>
              )}
            </form>
          </div>
        </section>

        <section>
          {/* NOS CENTRES - OUTSIDE THE FORM */}
          <div className="w-full max-w-5xl mx-auto mt-20 md:mt-32">
            <div className="text-center mb-10">
              <h3 className="text-3xl md:text-5xl font-heading font-bold text-white mb-3">
                NOS CENTRES PASSBAC
              </h3>
              <p className="text-[#a8fbd3] font-mono text-sm md:text-base tracking-widest uppercase opacity-80">
                Où nous trouver
              </p>
            </div>

            <div className="h-[400px] md:h-[500px] w-full rounded-[2rem] overflow-hidden border border-white/10 shadow-[0_0_40px_rgba(0,0,0,0.5)] relative bg-black/40 group">
              <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''}>
                <Map
                  defaultCenter={{ lat: 33.5731, lng: -7.61 }} // Casablanca
                  defaultZoom={12}
                  gestureHandling={'greedy'}
                  disableDefaultUI={false}
                  mapId="DEMO_MAP_ID"
                >
                  {ecoles.map((ecole) => (
                    <AdvancedMarker key={ecole.id} position={{ lat: ecole.lat, lng: ecole.lng }}>
                      <div className="relative group/marker flex flex-col items-center">
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 rounded-full bg-[#4fb7b3]/30 animate-ping opacity-0 group-hover/marker:opacity-100 transition-opacity" />

                        <div className="relative z-10 transition-transform duration-300 group-hover/marker:scale-125 group-hover/marker:-translate-y-2">
                          <Pin background={'#4fb7b3'} borderColor={'#ffffff'} glyphColor={'white'} scale={1.2} />
                        </div>

                        {/* Custom Info Window / Tooltip */}
                        <div className="absolute bottom-[120%] mb-2 bg-black/90 backdrop-blur-md border border-[#4fb7b3]/40 text-white px-5 py-3 rounded-2xl whitespace-nowrap opacity-0 group-hover/marker:opacity-100 transition-all duration-300 pointer-events-none z-20 shadow-[0_10px_30px_rgba(0,0,0,0.6)] transform translate-y-4 group-hover/marker:translate-y-0">
                          <div className="font-bold text-[#a8fbd3] font-heading text-lg">{ecole.name}</div>
                          <div className="text-xs font-mono text-gray-400 mt-1">{ecole.address}</div>

                          {/* Triangle pointer */}
                          <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-black/90 border-b border-r border-[#4fb7b3]/40 transform rotate-45" />
                        </div>
                      </div>
                    </AdvancedMarker>
                  ))}
                </Map>
              </APIProvider>
            </div>
          </div>
        </section>


        <footer className="relative z-10 border-t border-white/10 py-12 md:py-16 bg-black/80 backdrop-blur-xl">
          <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row justify-between items-start md:items-end gap-8">
            <div className="font-heading text-3xl md:text-4xl font-bold tracking-tighter mb-4 text-white">PASSBAC</div>

            <div className="flex gap-6 md:gap-8 flex-wrap">
              <a href="https://www.instagram.com/pass_bac" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white font-bold uppercase text-xs tracking-widest transition-colors cursor-pointer" data-hover="true">
                Instagram
              </a>
            </div>
          </div>
        </footer>

        {/* Professeur Detail Modal */}
        <AnimatePresence>
          {selectedProfesseur && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedProfesseur(null)}
              className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-md cursor-auto"
            >
              <motion.div
                initial={{ scale: 0.9, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.9, y: 20 }}
                onClick={(e) => e.stopPropagation()}
                className="relative w-full max-w-5xl bg-[#1a1b3b] border border-white/10 overflow-hidden flex flex-col md:flex-row shadow-2xl shadow-[#4fb7b3]/10 group/modal"
              >
                {/* Close Button */}
                <button
                  onClick={() => setSelectedProfesseur(null)}
                  className="absolute top-4 right-4 z-20 p-2 rounded-full bg-black/50 text-white hover:bg-white hover:text-black transition-colors"
                  data-hover="true"
                >
                  <X className="w-6 h-6" />
                </button>

                {/* Navigation Buttons */}
                <button
                  onClick={(e) => { e.stopPropagation(); navigateProfesseur('prev'); }}
                  className="absolute left-4 bottom-4 translate-y-0 md:top-1/2 md:bottom-auto md:-translate-y-1/2 z-20 p-3 rounded-full bg-black/50 text-white hover:bg-white hover:text-black transition-colors border border-white/10 backdrop-blur-sm"
                  data-hover="true"
                  aria-label="Previous Professeur"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>

                <button
                  onClick={(e) => { e.stopPropagation(); navigateProfesseur('next'); }}
                  className="absolute right-4 bottom-4 translate-y-0 md:top-1/2 md:bottom-auto md:-translate-y-1/2 z-20 p-3 rounded-full bg-black/50 text-white hover:bg-white hover:text-black transition-colors border border-white/10 backdrop-blur-sm md:right-8"
                  data-hover="true"
                  aria-label="Next Professeur"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>

                {/* Image Side */}
                <div className="w-full md:w-1/2 h-64 md:h-auto relative overflow-hidden">
                  <AnimatePresence mode="wait">
                    <motion.img
                      key={selectedProfesseur.id}
                      src={selectedProfesseur.image}
                      alt={selectedProfesseur.name}
                      initial={{ opacity: 0, scale: 1.1 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.4 }}
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  </AnimatePresence>
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1a1b3b] via-transparent to-transparent md:bg-gradient-to-r" />
                </div>

                {/* Content Side */}
                <div className="w-full md:w-1/2 p-8 pb-24 md:p-12 flex flex-col justify-center relative">
                  <motion.div
                    key={selectedProfesseur.id}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: 0.1 }}
                  >
                    <div className="flex items-center gap-3 text-[#4fb7b3] mb-4">
                      <Calendar className="w-4 h-4" />
                      <span className="font-mono text-sm tracking-widest uppercase">{selectedProfesseur.username}</span>
                    </div>

                    <h3 className="text-4xl md:text-6xl font-heading font-bold uppercase leading-none mb-2 text-white">
                      {selectedProfesseur.name}
                    </h3>

                    <p className="text-lg text-[#a8fbd3] font-medium tracking-widest uppercase mb-6">
                      {selectedProfesseur.matiere}
                    </p>

                    <div className="h-px w-20 bg-white/20 mb-6" />

                    <p className="text-gray-300 leading-relaxed text-lg font-light mb-8">
                      {selectedProfesseur.description}
                    </p>
                  </motion.div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
};

export default App;